import React, { ChangeEvent, JSX, useCallback, useEffect, useState } from 'react';

import { Box, Button, Checkbox, FormControlLabel, FormGroup, Stack, TextField, Typography } from '@mui/material';

import { LeBrand, RateCodeFormValues } from '../types';

const BRAND_OPTIONS: Array<{ label: string; value: LeBrand }> = [
  { label: 'Luxury Escapes', value: 'luxuryescapes' },
  { label: 'Cudo', value: 'cudotravel' },
  { label: 'Scoopon', value: 'scoopontravel' },
  { label: 'Deals', value: 'dealstravel' },
  { label: 'Treat Me', value: 'treatmetravel' },
  { label: 'Kogan Travel', value: 'kogantravel' },
  { label: 'LE Business Traveller', value: 'lebusinesstraveller' },
  { label: 'ZoomZoom', value: 'zoomzoom' },
  { label: 'LE Agent Hub', value: 'leagenthub' },
  { label: 'CVFR Holidays', value: 'leagenthub_cvfr' },
];

const INITIAL_FORM_STATE: RateCodeFormValues = {
  id: '',
  code: '',
  vendorId: '',
  isFlash: false,
  isEnabled: false,
  disabledInBrands: [],
};

type Props = {
  rateCodeFormValues?: RateCodeFormValues;
  onSubmit: (formValues: RateCodeFormValues) => void;
};

function RateCodeForm({ rateCodeFormValues, onSubmit }: Props): JSX.Element {
  const [formValues, setFormValues] = useState<RateCodeFormValues>(INITIAL_FORM_STATE);

  const handleCheck = useCallback(
    (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (event.target.name) {
        setFormValues({ ...formValues, [event.target.name]: checked });
      }
    },
    [formValues],
  );

  const handleDisabledBrandChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const brand = event.target.name as LeBrand;
      const isDisabled = formValues.disabledInBrands.includes(brand);

      setFormValues({
        ...formValues,
        disabledInBrands: isDisabled
          ? formValues.disabledInBrands.filter((disabledBrand) => disabledBrand !== brand)
          : [...formValues.disabledInBrands, brand],
      });
    },
    [formValues],
  );

  const handleSubmit = useCallback(() => {
    onSubmit(formValues);
  }, [formValues, onSubmit]);

  useEffect(() => {
    if (rateCodeFormValues) {
      setFormValues(rateCodeFormValues);
    }
  }, [rateCodeFormValues]);

  return (
    <Box>
      <Stack mt={2} spacing={2} direction="row">
        <TextField
          disabled
          fullWidth
          name="rateCode"
          label="Rate Code"
          value={formValues.code}
          InputProps={{ readOnly: true }}
        />

        <TextField
          disabled
          fullWidth
          name="vendorId"
          label="Vendor ID"
          value={formValues.vendorId}
          InputProps={{ readOnly: true }}
        />
      </Stack>

      <Box mt={2}>
        <FormControlLabel
          label="Is Flash?"
          control={<Checkbox required name="isFlash" onChange={handleCheck} checked={formValues.isFlash} />}
        />
      </Box>

      <Box mt={2}>
        <FormControlLabel
          label="Is Enabled?"
          control={<Checkbox required name="isEnabled" onChange={handleCheck} checked={formValues.isEnabled} />}
        />
      </Box>

      <Box mt={2}>
        <FormGroup>
          <Typography fontWeight="bold">Disabled in brands</Typography>

          <Stack direction="row" flexWrap="wrap">
            {BRAND_OPTIONS.map((brand) => (
              <FormControlLabel
                key={brand.value}
                label={brand.label}
                control={
                  <Checkbox
                    name={brand.value}
                    checked={formValues.disabledInBrands.includes(brand.value)}
                    onChange={handleDisabledBrandChange}
                  />
                }
              />
            ))}
          </Stack>
        </FormGroup>
      </Box>

      <Box mt={2}>
        <Button variant="contained" onClick={handleSubmit}>
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default RateCodeForm;
